// The ecology's canopy windows on the client (server: simulation/src/ecocanopywire.py has the format, ecocanopy.py what a vertex holds, groundwin.py who is sent what; design: claude/ecology-lod-design.md).
//
// A canopy window is the ground window of the same key (groundWire.ts: ring k, RING_N x RING_N vertices dx = 0.04 m * 2^k apart, vertex (i, j) at tile (x0 + i dx, y0 + j dx)) carrying what grows there
// instead of how high the ground is: the leaf area index, the canopy height, the woody crown width (tree and shrub), the tone (how lush) and the shares of the leaf area that are tree, shrub, fern and grass. The
// meta also carries the artist's look (the leaf palette, the cover law), so the client has no colours of its own. Rings 8 to 15 only (10 m to 1.3 km between
// vertices); nearer than that the individual plants (ecoWire.ts) are the picture.
//
// Frames start with "ECC" (not the ground's "GCP"); the framing is the ground's: begin, then one deflated part per plane. A plane is n rows of n bytes, each row stored as the difference from the
// vertex to its left (modulo 256). Planes in order: leaf area index, canopy height, woody crown width (each a log code: 0 is zero, 1 to 255 run from `lo` to `hi` in equal ratios), the tone (linear, 0 dry to 255 lush), then the form shares (linear, 0 to 255).
const HEAD = 12;
const KIND_BEGIN = 1, KIND_PART = 2;
const STREAM_BITS = 5;
const STREAM_MASK = (1 << STREAM_BITS) - 1;
const MSG_TYPE = 'canopy_win';
const MAX_N = 1024;
const MAX_FORMS = 8;

/** The ground rings that carry a canopy window. Must agree with ecocanopy.py CANOPY_RINGS. */
export const CANOPY_RING_FIRST = 8;
export const CANOPY_RING_LAST = 15;

/** True for one of this module's frames ("ECC", version 1, ..., 0xFF in byte 7, which no physics frame's tick can have). */
export function isCanopyFrame(buf: ArrayBuffer): boolean {
  if (buf.byteLength < HEAD) return false;
  const b = new Uint8Array(buf, 0, 8);
  return b[0] === 0x45 && b[1] === 0x43 && b[2] === 0x43 && b[3] === 1 && b[7] === 0xFF;
}

async function inflateZ(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export interface CanopyPartSpec { o: number; n: number; st: number; f: 'none' | 'b8' }
export interface CanopyMeta {
  type: 'canopy_win';
  v: number;
  stream: number;
  key: [number, number, number];
  n: number;
  dx: number;
  /** tile position of vertex (0, 0) */
  x0: number; y0: number;
  /** the number of forms whose shares follow the four planes before them */
  nf: number;
  /** the artist's look: the leaf area indices of dry and lush ground, the cover law's constant, the shade between crowns, and four linear rgb tones per form (tree, shrub, fern, grass): dry shade, dry lit, lush shade, lush lit */
  look: CanopyLook;
  /** the log planes' ranges [lo, hi] */
  q: { lai: [number, number]; h: [number, number]; w: [number, number] };
  /** the first 16 hex digits of the ecology file's sha256 the window was painted from */
  src: string;
  bin: { len: { d: number }; parts: CanopyPartSpec[] };
}
export interface CanopyLook { dry: number; lush: number; k: number; gap: number; pal: number[][][] }
/** Planes before the form shares: leaf area index, canopy height, woody crown width, tone. */
export const CANOPY_PLANES = 4;
/** A whole window: `planes` is (4 + nf) planes of n * n bytes, row j = tile y, column i = tile x, with the row differences undone. */
export interface CanopyWindow { meta: CanopyMeta; planes: Uint8Array }

interface Assembly { id: number; meta: CanopyMeta; data: Uint8Array; left: number }

/** Row differences of bytes undone in place: a running sum along each row of `row` bytes, modulo 256. */
function unfilterBytes(a: Uint8Array, row: number): void {
  for (let r = 0; r < a.length; r += row) for (let i = r + 1; i < r + row; i++) a[i] = (a[i]! + a[i - 1]!) & 255;
}

/** Collects the frames of the newest package of each ring. A begin frame starts a package and drops an unfinished one of the same ring; parts of any other package are ignored. When the last part of a
 *  package is in, `onWindow` gets the window. */
export class CanopyAssembler {
  private cur: (Assembly | null)[] = new Array(1 << STREAM_BITS).fill(null);
  /** packages abandoned unfinished (a newer one of the same ring began first), for the console */
  dropped = 0;

  constructor(private readonly onWindow: (w: CanopyWindow) => void, private readonly onError?: (e: unknown) => void) {}

  accept(buf: ArrayBuffer): void {
    const v = new DataView(buf);
    const kind = v.getUint8(4), id = v.getUint32(8, true), s = id & STREAM_MASK;
    if (kind === KIND_BEGIN) {
      let meta: CanopyMeta;
      try {
        meta = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 16, v.getUint32(12, true)))) as CanopyMeta;
        if (meta.type !== MSG_TYPE || !meta.bin || !Array.isArray(meta.bin.parts) || !meta.q) throw new Error('not a canopy window');
        if (meta.v !== 2) throw new Error(`canopy window of version ${meta.v}: this client reads version 2 (the server and the client are not from the same build)`);
        const nn = meta.n * meta.n;
        if (!(meta.n > 0 && meta.n <= MAX_N) || !(meta.nf >= 1 && meta.nf <= MAX_FORMS) || meta.bin.len.d !== (CANOPY_PLANES + meta.nf) * nn || meta.bin.parts.length !== CANOPY_PLANES + meta.nf || meta.stream !== s) {
          throw new Error('canopy window: the meta does not describe a window');
        }
      } catch (e) { this.cur[s] = null; this.onError?.(e); return; }
      const old = this.cur[s];
      if (old && old.left > 0) this.dropped++;
      this.cur[s] = { id, meta, data: new Uint8Array(meta.bin.len.d), left: meta.bin.parts.length };
      return;
    }
    const a = this.cur[s];
    if (kind !== KIND_PART || !a || a.id !== id) return;
    const spec = a.meta.bin.parts[v.getUint16(12, true)];
    if (!spec) return;
    inflateZ(new Uint8Array(buf, 14)).then(raw => {
      if (this.cur[s] !== a) return;                                       // a newer package of this ring began while this one was inflating
      if (raw.length !== spec.n || spec.o + spec.n > a.data.length) throw new Error(`canopy window part @${spec.o}: ${raw.length} bytes, expected ${spec.n}`);
      if (spec.f === 'b8') unfilterBytes(raw, spec.st);
      a.data.set(raw, spec.o);
      if (--a.left === 0) { this.cur[s] = null; this.onWindow({ meta: a.meta, planes: a.data }); }
    }).catch(e => { if (this.cur[s] === a) this.cur[s] = null; this.onError?.(e); });
  }
}

/** A log code back to its value (0 is zero). */
export function decodeLog(code: number, range: [number, number]): number {
  return code === 0 ? 0 : range[0] * Math.exp((code - 1) / 254 * Math.log(range[1] / range[0]));
}
